import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { QrCode, Copy, Mail, Check, LogIn, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";

// Declare adsbygoogle for TypeScript
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

const Index = () => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    user,
    profile,
    signOut
  } = useAuth();
  const [teamName, setTeamName] = useState("");
  const [sprintName, setSprintName] = useState("");
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [createdRoomId, setCreatedRoomId] = useState<string | null>(null);
  const roomUrl = createdRoomId ? `${window.location.origin}/room/${createdRoomId}` : "";
  const startSession = async () => {
    if (!teamName.trim() || !sprintName.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter team name and sprint information",
        variant: "destructive"
      });
      return;
    }

    try {
      // Create room in database (guest mode allows creator_id to be null)
      const { data: room, error: roomError } = await supabase
        .from("rooms")
        .insert({
          creator_id: user?.id || null,
          team_name: teamName.trim(),
          sprint_name: sprintName.trim(),
          is_revealed: false
        })
        .select()
        .single();

      if (roomError) throw roomError;

      // Add default cards for the room
      const defaultCards = DEFAULT_CARDS.map((card, index) => ({
        room_id: room.id,
        value: card.value,
        label: card.label,
        position: index
      }));

      const { error: cardsError } = await supabase
        .from("room_cards")
        .insert(defaultCards);

      if (cardsError) throw cardsError;

      // Set the created room ID for sharing
      setCreatedRoomId(room.id);

      toast({
        title: "Room created!",
        description: "Redirecting to room...",
        duration: 7000
      });

      // Navigate to the room immediately
      navigate(`/room/${room.id}`);
    } catch (error: any) {
      console.error("Error creating room:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create room",
        variant: "destructive"
      });
    }
  };

  const DEFAULT_CARDS = [
    { value: "0", label: "0" },
    { value: "0.1", label: "0.1" },
    { value: "0.25", label: "0.25" },
    { value: "0.5", label: "0.5" },
    { value: "1", label: "1" },
    { value: "1.5", label: "1.5" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "☕", label: "☕" },
  ];
  const copyRoomUrl = async () => {
    if (!createdRoomId) {
      toast({
        title: "Create room first",
        description: "Please create a room before sharing",
        variant: "destructive"
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(roomUrl);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Room URL copied to clipboard"
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Error",
        description: "Could not copy URL",
        variant: "destructive"
      });
    }
  };
  const shareViaEmail = () => {
    if (!createdRoomId) {
      toast({
        title: "Create room first",
        description: "Please create a room before sharing",
        variant: "destructive"
      });
      return;
    }
    const subject = encodeURIComponent(`${teamName} - ${sprintName} Scrum Poker Session`);
    const body = encodeURIComponent(`Hello,\n\nJoin the scrum poker estimation session:\n\n${roomUrl}\n\nRoom ID: ${createdRoomId}\n\nHappy estimating!`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };
  const toggleQR = () => {
    if (!createdRoomId) {
      toast({
        title: "Create room first",
        description: "Please create a room before sharing",
        variant: "destructive"
      });
      return;
    }
    setShowQR(!showQR);
  };

  // Initialize AdSense
  useEffect(() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, []);
  return <div className="min-h-screen bg-gradient-table flex flex-col">
      {/* Top Right Controls */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo/Header */}
          <header className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-primary">Free Scrum Poker Online</h1>
            <p className="text-lg text-muted-foreground">
              Estimate story points collaboratively with your agile team using planning poker
            </p>
          </header>

        {/* Session Info */}
        <Card className="p-6 bg-card border-border shadow-card">
          <div className="space-y-4">
            <div>
              <Label htmlFor="teamName" className="text-foreground">
                Team Name
              </Label>
              <Input id="teamName" value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="e.g., Frontend Team" className="mt-2" maxLength={50} />
            </div>
            <div>
              <Label htmlFor="sprintName" className="text-foreground">
                Sprint
              </Label>
              <Input 
                id="sprintName" 
                value={sprintName} 
                onChange={e => setSprintName(e.target.value)} 
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && teamName.trim() && sprintName.trim()) {
                    startSession();
                  }
                }}
                placeholder="e.g., Sprint 24 - October 2024" 
                className="mt-2" 
                maxLength={50} 
              />
            </div>
            
            {/* Create/Join Room Buttons */}
            <div className="space-y-2">
              {!createdRoomId ? (
                <Button onClick={startSession} disabled={!teamName.trim() || !sprintName.trim()} className="w-full">
                  Create Room
                </Button>
              ) : (
                <>
                  {/* Share Options */}
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <Button onClick={toggleQR} variant="outline" size="sm" className="flex flex-col h-auto py-3 gap-1">
                      <QrCode size={16} />
                      <span className="text-xs">QR Code</span>
                    </Button>
                    
                    <Button onClick={copyRoomUrl} variant="outline" size="sm" className="flex flex-col h-auto py-3 gap-1">
                      {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                      <span className="text-xs">Copy URL</span>
                    </Button>
                    
                    <Button onClick={shareViaEmail} variant="outline" size="sm" className="flex flex-col h-auto py-3 gap-1">
                      <Mail size={16} />
                      <span className="text-xs">Email</span>
                    </Button>
                  </div>
                  <Button onClick={() => navigate(`/room/${createdRoomId}`)} className="w-full">
                    Join Room
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

          {/* Google AdSense */}
          <div className="flex justify-center my-6">
            <ins className="adsbygoogle"
                 style={{display: 'block'}}
                 data-ad-client="ca-pub-5039342413822099"
                 data-ad-slot="2373006410"
                 data-ad-format="auto"
                 data-full-width-responsive="true"></ins>
          </div>

          {/* Footer Links */}
          <footer className="text-center text-sm text-muted-foreground">
            <nav className="flex justify-center gap-6">
              <a href="/how-it-works" className="hover:text-primary transition-colors">How It Works</a>
              <a href="/features" className="hover:text-primary transition-colors">Features</a>
              <a href="/guide" className="hover:text-primary transition-colors">Estimation Guide</a>
            </nav>
          </footer>
        </div>
      </main>

      {/* QR Code Modal */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-64 h-64 bg-white p-4 rounded-lg shadow-sm">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(roomUrl)}`} alt="QR Code" className="w-full h-full" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Scan this QR code with your phone to join the room
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
};
export default Index;